import React, { FC } from 'react';
import { Flex, Card, Box, Text, Heading } from '@rebass/emotion';
import styles, { BundleLabel, PRODUCT_TILE_RATIO } from './ProductTile.styles';
import ProductImage from '../ProductImage';
import ProductPrice from '../ProductPrice';
import { ratioToPercentage } from '../../../styles/utils';
import VideoOverlayTrigger from '../../VideoOverlayTrigger';
import AudioPlayerTrigger from '../../AudioPlayerTrigger';
import { GetProductsForFinder_getProducts_result_items as Product } from '../ProductsProvider/__generated__/GetProductsForFinder';

const BUNDLE_LABEL = 'Bundle';

// used to get attribute from product.attributes array
const getAttr = (key: string, attrs): any => {
  if (attrs) {
    const target = attrs.find((a) => a!.key === key) || {};
    return target.value;
  }
  return undefined;
};

export interface ProductTileProps {
  product: DeepOmit<Product, '__typename'>;
  ratio?: number[];
}

const ProductTile: FC<ProductTileProps> = ({
  product,
  ratio = PRODUCT_TILE_RATIO,
}) => {
  const { title, sku, attributes, image, audioId, videoId, price } = product;

  const sticker = getAttr('sticker', attributes);
  const description = getAttr('description', attributes);
  const detailPageUrl = getAttr('detail_page', attributes);
  const pricingPageUrl = getAttr('pricing_page', attributes);

  return (
    <Flex css={styles.wrapper} data-sku={sku} className="product-tile-wrapper">
      <Box pb={ratioToPercentage(ratio)} css={styles.imageWrapper}>
        {sticker && <Text css={styles.sticker}>{sticker}</Text>}
        <a href={detailPageUrl}>
          <Card className="product-tile__card" css={styles.card}>
            <ProductImage image={getAttr('image_lazy', attributes) || image} />
          </Card>
        </a>

        <Flex css={styles.mediaWrapper}>
          {audioId && <AudioPlayerTrigger audioId={audioId} />}
          {videoId && <VideoOverlayTrigger videoId={videoId} />}
        </Flex>
      </Box>

      <Flex css={styles.contentWrapper} className="product-tile-content">
        <Box css={styles.textContent}>
          <Heading css={styles.title} className="product-tile-title">
            <a href={detailPageUrl}>{title}</a>
            {price?.bundle && <BundleLabel>{BUNDLE_LABEL}</BundleLabel>}
          </Heading>

          <Text mb={3} css={styles.description}>
            <a
              href={detailPageUrl}
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </Text>
        </Box>
        <Box css={styles.priceWrapper} className="product-price-wrapper">
          <a href={pricingPageUrl}>
            <ProductPrice
              text={getAttr('custom_price_text', attributes)}
              shouldReplacePrice={getAttr('override_price', attributes)}
              isFree={getAttr('free', attributes)}
              price={price}
            />
          </a>
        </Box>
      </Flex>
    </Flex>
  );
};

export default ProductTile;
